import { z } from "zod";

import {
  blankToNull,
  requiredCoordinate,
  requiredNullableText,
  requiredText,
} from "@/domain/validation";

// Phone validation accepts:
//   • E.164 with optional internal spaces, e.g. "+420 777 123 456" or "+44 7700 900123"
//   • Czech 9-digit local format: "777 123 456" or "777123456"
// Validation runs after stripping all whitespace, so users can format freely.
const PHONE_REGEX = /^(\+\d{9,15}|\d{9})$/;

// Mirrors the loose `businesses_notification_email_format` DB check: a shape
// test that catches typos without rejecting addresses that deliver fine.
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const POSTAL_CODE_REGEX = /^\d{5}$/;

// A street or city written in nothing but digits is a house number or a PSČ that
// landed in the wrong box.
const isWrittenAsNumber = (value: string) => /^[\d\s./,-]+$/.test(value);

const placeName = ({
  maxLength,
  missing,
  refusal,
}: {
  maxLength: number;
  missing: string;
  refusal: string;
}) =>
  requiredNullableText({ maxLength, missing }).refine(
    (value) => value === null || !isWrittenAsNumber(value),
    { message: refusal },
  );

export const businessProfileSchema = z
  .object({
    name: requiredText({ missing: "Název podniku je povinný", maxLength: 100 }),
    addressStreet: placeName({
      maxLength: 200,
      missing: "Zadejte ulici a číslo popisné",
      refusal: "Zadejte název ulice, ne jen číslo (např. Vinohradská 123)",
    }),
    addressCity: placeName({
      maxLength: 100,
      missing: "Zadejte město",
      refusal: "Zadejte název města, ne číslo (např. Praha)",
    }),
    addressCode: requiredNullableText({ maxLength: 20, missing: "Zadejte PSČ" })
      .transform((value) => (value === null ? null : value.replace(/\s+/g, "")))
      .refine((value) => value === null || POSTAL_CODE_REGEX.test(value), {
        message: "PSČ zadejte jako pět číslic (např. 120 00)",
      }),
    locationLatitude: requiredCoordinate({
      limit: 90,
      outOfRange: "Zeměpisná šířka musí být číslo mezi -90 a 90",
      missing: "Zadejte zeměpisnou šířku, nebo polohu vyberte tlačítkem „Vybrat na mapě“",
    }),
    locationLongitude: requiredCoordinate({
      limit: 180,
      outOfRange: "Zeměpisná délka musí být číslo mezi -180 a 180",
      missing: "Zadejte zeměpisnou délku, nebo polohu vyberte tlačítkem „Vybrat na mapě“",
    }),
    phone: requiredNullableText({
      maxLength: 30,
      missing: "Zadejte telefonní číslo podniku",
    }).refine((v) => v === null || PHONE_REGEX.test(v.replace(/\s+/g, "")), {
      message: "Neplatný formát čísla (např. +420 777 123 456 nebo 777 123 456)",
    }),
    notificationEmail: z
      .string()
      .nullable()
      .refine((v) => v === null || v.trim() === "" || EMAIL_REGEX.test(v.trim()), {
        message: "Neplatná e-mailová adresa (např. rezervace@podnik.cz)",
      })
      .transform((v) => (v && v.trim() !== "" ? v.trim() : null)),
    hasDiscountLimit: z.boolean(),
    discountUsesPerCustomer: z.preprocess(
      blankToNull,
      z
        .number({ invalid_type_error: "Zadejte počet využití číslem" })
        .int("Zadejte celý počet využití")
        .min(1, "Nejméně jedno využití")
        .nullable(),
    ),
    discountUsesPeriodDays: z.preprocess(
      blankToNull,
      z
        .number({ invalid_type_error: "Zadejte počet dní číslem" })
        .int("Zadejte celý počet dní")
        .min(1, "Nejméně jeden den")
        .nullable(),
    ),
  })
  .superRefine((profile, ctx) => {
    if (!profile.hasDiscountLimit) return;
    if (profile.discountUsesPerCustomer === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["discountUsesPerCustomer"],
        message: "Zadejte počet využití",
      });
    }
    if (profile.discountUsesPeriodDays === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["discountUsesPeriodDays"],
        message: "Zadejte počet dní",
      });
    }
  })
  // Bez omezení is the partner's choice, not an empty field, so choosing it
  // clears whatever a limit they abandoned left behind.
  .transform((profile) =>
    profile.hasDiscountLimit
      ? profile
      : { ...profile, discountUsesPerCustomer: null, discountUsesPeriodDays: null },
  );

export type BusinessProfileInput = z.infer<typeof businessProfileSchema>;
